import { useEffect, useState, useCallback } from 'react';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { useEventsModule } from '../context/EventsModuleContext';
import { getCalendarEvents, getCalendarSettings, getAnnouncements } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Clock, MapPin, Church, ChevronDown, ChevronUp, Megaphone, ArrowRight } from 'lucide-react';
import usePageBanner from '../hooks/usePageBanner';
import { AutoTranslate, AutoT, useAutoTranslate } from '../hooks/useAutoTranslate';
import TranslatedContent from '../components/TranslatedContent';
import AddToCalendarButton from '../components/AddToCalendarButton';
import { createSanitizedMarkup } from '../utils/sanitize';

// Renders a calendar-event description as sanitized HTML with auto-translation.
// Allows tags like <b>, <i>, <br>, <a> from the iCal/CalDAV source (sanitized via DOMPurify).
const EventDescriptionHtml = ({ description, className = '' }) => {
  const translated = useAutoTranslate(description || '');
  if (!description) return null;
  return (
    <div
      className={className}
      dangerouslySetInnerHTML={createSanitizedMarkup(translated || description)}
    />
  );
};

// Compact Announcement Card for sidebar
const AnnouncementCardCompact = ({ announcement, t, isRTL }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Check if content is long enough to need expansion
  const contentText = announcement.content?.replace(/<[^>]*>/g, '') || '';
  const needsExpansion = contentText.length > 100;

  // Format event date if present
  const eventDate = announcement.event_date ? new Date(announcement.event_date) : null;
  const formattedEventDate = eventDate ? eventDate.toLocaleDateString('sv-SE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  }) : null;

  return (
    <Card className="overflow-hidden border-0 shadow-md hover:shadow-xl card-hover transition-shadow" data-testid={`announcement-${announcement.id}`}>
      {announcement.featured_image && (
        <div className="h-32 overflow-hidden">
          <img
            src={announcement.featured_image}
            alt={announcement.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <CardContent className="p-4">
        {/* Only show event date badge if event_date is set */}
        {formattedEventDate && (
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} text-xs text-yellow-700 font-medium mb-2`}>
            <Calendar className="w-3 h-3" />
            <span>{formattedEventDate}</span>
          </div>
        )}
        {announcement.churches && announcement.churches.length < 3 && (
          <span className="inline-block px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs mb-2">
            {announcement.churches.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(', ')}
          </span>
        )}
        <TranslatedContent
          content={{ sv: announcement.title }}
          as="h4"
          className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2"
        />
        <TranslatedContent
          content={{ sv: announcement.content }}
          as="div"
          className={`text-xs text-gray-600 leading-relaxed ${!isExpanded && needsExpansion ? 'line-clamp-3' : ''}`}
          html={true}
        />
        {needsExpansion && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`mt-2 text-xs font-medium text-yellow-600 hover:text-yellow-700 flex items-center gap-1 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            {isExpanded ? (
              <>
                {t('showLess') || 'Visa mindre'}
                <ChevronUp className="w-3 h-3" />
              </>
            ) : (
              <>
                {t('readMore') || 'Läs mer'}
                <ChevronDown className="w-3 h-3" />
              </>
            )}
          </button>
        )}
      </CardContent>
    </Card>
  );
};

const EventCard = ({ event, t, isRTL, language }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const startDate = new Date(event.start_datetime);
  // Always use 24-hour format regardless of language
  const timeStr = startDate.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit', hour12: false });
  
  // Check if description needs expansion (more than 100 chars of visible text)
  const descriptionText = (event.description || '').replace(/<[^>]*>/g, '');
  const needsExpansion = descriptionText.length > 100;

  return (
    <Card className="border-0 shadow-md hover:shadow-xl card-hover transition-shadow" data-testid={`event-${event.uid}`}>
      <CardContent className="p-4">
        <div className={`flex items-start justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 text-lg">
              <AutoTranslate>{event.title}</AutoTranslate>
            </h4>
            {event.description && (
              <div className="mt-1">
                <EventDescriptionHtml
                  description={event.description}
                  className={`text-sm text-gray-600 ${!isExpanded && needsExpansion ? 'line-clamp-2' : ''}`}
                />
                {needsExpansion && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`mt-1 text-xs font-medium text-yellow-600 hover:text-yellow-700 flex items-center gap-1 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    {isExpanded ? (
                      <>
                        {t('showLess') || 'Visa mindre'}
                        <ChevronUp className="w-3 h-3" />
                      </>
                    ) : (
                      <>
                        {t('more') || 'mer...'}
                        <ChevronDown className="w-3 h-3" />
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            <div className={`flex flex-wrap gap-3 mt-3 text-sm text-gray-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Clock className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'} text-blue-500`} />
                <span>{timeStr}</span>
                <AddToCalendarButton event={event} isRTL={isRTL} />
              </div>
              {event.location && (
                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <MapPin className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'} text-green-500`} />
                  <span><AutoTranslate>{event.location}</AutoTranslate></span>
                </div>
              )}
              {event.language && (
                <div className="flex items-center">
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">
                    {event.language}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DayGroup = ({ date, events, t, isPast, isRTL, language }) => {
  // Parse date as UTC to get correct day name
  const [year, month, day] = date.split('-').map(Number);
  const dateObj = new Date(Date.UTC(year, month - 1, day));
  
  // Use locale-aware formatting
  const localeMap = {
    'sv': 'sv-SE',
    'en': 'en-US',
    'ar': 'ar-SA',
    'pl': 'pl-PL',
    'es': 'es-ES',
    'fr': 'fr-FR',
    'de': 'de-DE'
  };
  const locale = localeMap[language] || 'sv-SE';
  const dayName = dateObj.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' });
  const dateStr = dateObj.toLocaleDateString(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' });
  
  // Get today in UTC
  const now = new Date();
  const todayUTC = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
  const isToday = date === todayUTC;

  return (
    <div className="mb-6">
      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'} mb-4 pb-2 border-b-2 ${isToday ? 'border-green-500' : isPast ? 'border-gray-300' : 'border-yellow-400'}`}>
        <Calendar className={`w-5 h-5 ${isToday ? 'text-green-600' : isPast ? 'text-gray-400' : 'text-yellow-600'}`} />
        <h3 className={`text-lg font-serif font-bold capitalize ${isPast ? 'text-gray-500' : 'text-gray-900'}`}>
          {dayName}, {dateStr}
          {isToday && <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm font-normal text-green-600 bg-green-100 px-2 py-0.5 rounded`}>{t('today') || 'Idag'}</span>}
          {isPast && <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm font-normal text-gray-400`}>({t('passed') || 'passerat'})</span>}
        </h3>
      </div>

      {events.length > 0 && (
        <div className="space-y-2">
          {events.map((event, idx) => (
            <EventCard key={event.uid || idx} event={event} t={t} isRTL={isRTL} language={language} />
          ))}
        </div>
      )}
    </div>
  );
};

const ChurchCalendar = ({ church, t, weeksAhead, isRTL, language, weekOffset, setWeekOffset }) => {
  const [data, setData] = useState({ masses: [], events: [] });
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCalendarEvents(church, weekOffset);
      setData(result);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  }, [church, weekOffset]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Group non-mass events by date
  const groupEventsByDate = (eventsList) => {
    const grouped = {};
    eventsList.forEach(event => {
      if (event.calendar_type === 'mass') return; // Skip masses
      const date = event.start_datetime.split('T')[0];
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(event);
    });
    return grouped;
  };

  // Generate all dates in the selected week (Monday to Sunday)
  const getWeekDates = (offset) => {
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const utcDay = today.getUTCDay();
    const pythonWeekday = utcDay === 0 ? 6 : utcDay - 1;
    const weekStart = new Date(today);
    weekStart.setUTCDate(today.getUTCDate() - pythonWeekday + (offset * 7));
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setUTCDate(weekStart.getUTCDate() + i);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    return dates;
  };

  const allEvents = [...(data.masses || []), ...(data.events || [])];
  const groupedByDate = groupEventsByDate(allEvents);
  const weekDates = getWeekDates(weekOffset);

  return (
    <div>
      {/* Events Display */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loadingCalendar')}</p>
        </div>
      ) : (
        <div>
          {weekDates
            .filter(date => groupedByDate[date] && groupedByDate[date].length > 0)
            .map(date => {
              const now = new Date();
              const todayUTC = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
              const isPast = date < todayUTC;
              
              return (
                <div 
                  key={date} 
                  className={`mb-8 ${isPast ? 'opacity-60' : ''}`}
                >
                  <DayGroup
                    date={date}
                    events={groupedByDate[date]}
                    t={t}
                    isPast={isPast}
                    isRTL={isRTL}
                    language={language}
                  />
                </div>
              );
            })}
          {weekDates.filter(date => groupedByDate[date] && groupedByDate[date].length > 0).length === 0 && (
            <Card className="p-8 text-center border-0 shadow-xl card-hover">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">
                {t('noEventsThisWeek')}
              </h3>
              <p className="text-gray-600">
                {t('selectAnotherWeek')}
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

const EventsPage = () => {
  const { t, language, isRTL } = useLanguage();
  const { eventsEnabled } = useEventsModule();
  const [searchParams] = useSearchParams();
  const churchParam = searchParams.get('church');
  
  // Get saved church preference from localStorage
  const savedChurch = localStorage.getItem('preferredChurch');
  const initialChurch = churchParam || savedChurch || 'karlskrona';
  
  const [activeChurch, setActiveChurch] = useState(initialChurch);
  const [settings, setSettings] = useState({ weeks_ahead: 2 });
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncementChurch, setSelectedAnnouncementChurch] = useState('all');
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('events', language);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadSettings = async () => {
    try {
      const data = await getCalendarSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading calendar settings:', error);
    }
  };

  const loadAnnouncements = async () => {
    try {
      setAnnouncementsLoading(true);
      const data = await getAnnouncements(null, true);
      setAnnouncements(data || []);
    } catch (error) {
      console.error('Error loading announcements:', error);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadAnnouncements();
  }, []);

  // Update active church when URL param changes
  useEffect(() => {
    if (churchParam && ['karlskrona', 'karlshamn', 'olofstrom'].includes(churchParam)) {
      setActiveChurch(churchParam);
      localStorage.setItem('preferredChurch', churchParam);
    }
  }, [churchParam]);

  // Save church preference when user changes tab
  const handleChurchChange = (churchId) => {
    setActiveChurch(churchId);
    localStorage.setItem('preferredChurch', churchId);
  };

  // Filter announcements by selected church
  const filteredAnnouncements = selectedAnnouncementChurch === 'all'
    ? announcements
    : announcements.filter(a => a.churches?.includes(selectedAnnouncementChurch));

  const churches = [
    { id: 'karlskrona', name: 'Karlskrona' },
    { id: 'karlshamn', name: 'Karlshamn' },
    { id: 'olofstrom', name: 'Olofström' }
  ];

  // Generate week options
  const weeksAhead = settings.weeks_ahead || 2;
  const weekOptions = [];
  for (let i = 0; i <= weeksAhead; i++) {
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const utcDay = today.getUTCDay();
    const pythonWeekday = utcDay === 0 ? 6 : utcDay - 1;
    const weekStart = new Date(today);
    weekStart.setUTCDate(today.getUTCDate() - pythonWeekday + (i * 7));
    const weekEnd = new Date(weekStart);
    weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
    
    let label = '';
    if (i === 0) {
      label = t('currentWeekLabel') || 'Denna vecka';
    } else if (i === 1) {
      label = t('nextWeekLabel') || 'Nästa vecka';
    } else {
      label = `${i} ${t('weeksAhead') || 'veckor'}`;
    }
    
    const startStr = `${weekStart.getUTCDate()} ${weekStart.toLocaleDateString('sv-SE', { month: 'short', timeZone: 'UTC' })}`;
    const endStr = `${weekEnd.getUTCDate()} ${weekEnd.toLocaleDateString('sv-SE', { month: 'short', timeZone: 'UTC' })}`;
    
    weekOptions.push({
      value: i,
      label: `${label} (${startStr} - ${endStr})`
    });
  }

  return (
    <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage}
          alt="Events"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-black/40 to-black/70' : 'from-black/70 to-black/40'} flex items-center`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${isRTL ? 'text-right' : ''}`}>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle || <AutoTranslate>Kungörelsekalender</AutoTranslate>}
            </h1>
            <p className="responsive-hero-subtitle text-base sm:text-lg md:text-xl text-gray-200 fade-in">
              {bannerSubtitle || <AutoTranslate>Alla församlingens kungörelser och aktiviteter</AutoTranslate>}
            </p>
          </div>
        </div>
      </section>

      {/* Calendar Content - layout depends on Events module toggle */}
      <section className="py-12" data-testid="events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex flex-col lg:flex-row gap-8 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>

            {/* Announcements Column — full width when calendar is hidden */}
            <div className={eventsEnabled ? 'lg:w-2/3' : 'lg:w-full'}>
              <div>
                {/* Header */}
                <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                    <Megaphone className="w-6 h-6 text-yellow-600" />
                    <h2 className="text-2xl font-serif font-bold text-gray-900">
                      <AutoTranslate>Meddelanden</AutoTranslate>
                    </h2>
                  </div>
                </div>

                {/* Church Filter — 4-tab layout */}
                <Tabs
                  value={selectedAnnouncementChurch}
                  onValueChange={setSelectedAnnouncementChurch}
                  className="w-full mb-4"
                >
                  <TabsList
                    className="grid grid-cols-2 sm:grid-cols-4 gap-1 h-auto bg-yellow-50 p-1 rounded-lg"
                    data-testid="announcement-church-tabs"
                  >
                    <TabsTrigger
                      value="all"
                      className="py-2 px-3 text-sm font-medium data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900"
                      data-testid="ann-tab-all"
                    >
                      {t('allChurches')}
                    </TabsTrigger>
                    <TabsTrigger
                      value="karlskrona"
                      className="py-2 px-3 text-sm font-medium data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900"
                      data-testid="ann-tab-karlskrona"
                    >
                      Karlskrona
                    </TabsTrigger>
                    <TabsTrigger
                      value="karlshamn"
                      className="py-2 px-3 text-sm font-medium data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900"
                      data-testid="ann-tab-karlshamn"
                    >
                      Karlshamn
                    </TabsTrigger>
                    <TabsTrigger
                      value="olofstrom"
                      className="py-2 px-3 text-sm font-medium data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900"
                      data-testid="ann-tab-olofstrom"
                    >
                      Olofström
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Announcements List — full list, no inner scroll */}
                {announcementsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">{t('loading')}</p>
                  </div>
                ) : filteredAnnouncements.length === 0 ? (
                  <Card className="p-6 text-center border-0 shadow-xl card-hover">
                    <Megaphone className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">{t('noAnnouncements')}</p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {filteredAnnouncements.map((announcement) => (
                      <AnnouncementCardCompact
                        key={announcement.id}
                        announcement={announcement}
                        t={t}
                        isRTL={isRTL}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Calendar Column - 1/3 — hidden when Events module is disabled */}
            {eventsEnabled && (
            <div className="lg:w-1/3">
              <div className={`mb-4 ${isRTL ? 'text-right' : ''}`}>
                <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'}`}>
                  <Calendar className="w-6 h-6 text-yellow-600" />
                  <h2 className="text-xl font-serif font-bold text-gray-900">
                    <AutoTranslate>Kalendern</AutoTranslate>
                  </h2>
                </div>
              </div>
              <Tabs value={activeChurch} onValueChange={handleChurchChange} className="w-full">
                {/* Header row with tabs and week selector inline */}
                <div className={`flex flex-col sm:flex-row sm:items-center gap-3 mb-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                  {/* Church Tabs */}
                  <TabsList className="grid grid-cols-3 h-auto flex-shrink-0">
                    {churches.map((church) => (
                      <TabsTrigger
                        key={church.id}
                        value={church.id}
                        className="py-2 px-3 text-sm font-medium data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900"
                        data-testid={`tab-${church.id}`}
                      >
                        <Church className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {church.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {/* Week Selector - inline with tabs, no label */}
                  <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'}`}>
                    <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="relative">
                      <select
                        value={weekOffset}
                        onChange={(e) => setWeekOffset(parseInt(e.target.value))}
                        className={`appearance-none bg-white border border-gray-300 rounded-lg px-3 py-1.5 ${isRTL ? 'pl-8' : 'pr-8'} text-gray-900 font-medium focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 cursor-pointer text-xs`}
                        data-testid="week-selector"
                      >
                        {weekOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className={`absolute ${isRTL ? 'left-2' : 'right-2'} top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none`} />
                    </div>
                  </div>
                </div>

                {churches.map((church) => (
                  <TabsContent key={church.id} value={church.id}>
                    <ChurchCalendar
                      church={church.id}
                      t={t}
                      weeksAhead={settings.weeks_ahead || 2}
                      isRTL={isRTL}
                      language={language}
                      weekOffset={weekOffset}
                      setWeekOffset={setWeekOffset}
                    />
                  </TabsContent>
                ))}
              </Tabs>

              {/* Navigation to Liturgi */}
              <div className={`mt-6 pt-4 border-t border-gray-200 ${isRTL ? 'text-right' : ''}`}>
                <Link 
                  to="/liturgi" 
                  className={`inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-lg shadow-md transition-all hover:shadow-lg text-sm ${isRTL ? 'flex-row-reverse' : ''}`}
                  data-testid="nav-to-liturgi"
                >
                  <Church className="w-4 h-4" />
                  <AutoTranslate>Se liturgi</AutoTranslate>
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </Link>
              </div>
            </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EventsPage;
